import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Row, Col, CardBody, Spinner, Alert, Button, FormGroup, Label, Input, FormFeedback } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { getPurchasableAddons, initiateAddonPurchase } from "../../api/requests/addon-requests"
import { getPlanIntegrationCatalog } from "../../api/requests/subscription-requests"
import BackButton from "../../components/Common/BackButton"

const NAVY = "#221C5D"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const formatPrice = (v) => {
  if (v == null) return "-"
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " ₺"
}

/** Kart numarasını 4'erli gruplar hâlinde gösterir (ödeme formuyla aynı). */
const formatCardNumber = (value) => {
  const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "")
  const parts = []
  for (let i = 0; i < v.length; i += 4) parts.push(v.substring(i, i + 4))
  return parts.length ? parts.join(" ") : v
}

/**
 * Ek modül ödeme sayfası.
 *
 * <h3>Neden sayfa PayTR HTML'i ile değiştiriliyor</h3>
 * Backend başarıda PayTR'nin 3D Secure formunu ham HTML olarak döner; bu form
 * kendi kendine submit olur. Abonelik akışı da aynı yolu kullanır
 * ({@code document.write}) — iki ödeme akışının davranışının ayrışmaması için
 * desen bilinçli olarak kopyalanmıştır.
 *
 * <h3>Hata gövdesi neden içerik tipine bakılarak çözülüyor</h3>
 * Aynı uç başarıda HTML, hatada JSON döndürür. İçerik tipi kontrol edilmezse
 * "zaten bekleyen ödemeniz var" gibi anlamlı mesajlar ham metin olarak
 * kullanıcıya basılırdı.
 */
const AddonCheckout = () => {
  const navigate = useNavigate()
  const { moduleId } = useParams()

  const [module, setModule] = useState(null)
  const [integrationLabels, setIntegrationLabels] = useState({})
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(null)

  document.title = "Ek Modül Ödemesi | Tahsilat360"

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // Vitrin ucu kullanılır: satın alınabilir olmayan bir modüle ödeme
        // sayfası açılmamalı, sayfa da bunu göstererek engeller.
        const [res, catalogRes] = await Promise.all([
          getPurchasableAddons(),
          getPlanIntegrationCatalog().catch(() => null),
        ])
        if (cancelled) return
        setIntegrationLabels(
          Object.fromEntries((catalogRes?.data || []).map((i) => [i.code, i.label]))
        )
        const found = (res.data || []).find((m) => m.id === moduleId)
        if (!found) {
          setErrorMessage("Bu modül satışta değil veya bulunamadı.")
        } else {
          setModule(found)
        }
      } catch (err) {
        if (!cancelled) setErrorMessage(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [moduleId])

  const formik = useFormik({
    initialValues: {
      card_number: "",
      cc_owner: "",
      expiry_month: "",
      expiry_year: "",
      cvv: "",
    },
    validationSchema: Yup.object({
      card_number: Yup.string()
        .transform((v) => (v || "").replace(/\s/g, ""))
        .matches(/^\d{15,19}$/, "Kart numarası geçersiz")
        .required("Kart numarası zorunludur"),
      cc_owner: Yup.string().trim().required("Kart sahibi zorunludur"),
      expiry_month: Yup.string().matches(/^(0[1-9]|1[0-2])$/, "Ay 01-12 arası olmalı").required("Ay zorunludur"),
      expiry_year: Yup.string().matches(/^\d{2,4}$/, "Yıl geçersiz").required("Yıl zorunludur"),
      cvv: Yup.string().matches(/^\d{3,4}$/, "CVV geçersiz").required("CVV zorunludur"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setErrorMessage(null)
      try {
        const resp = await initiateAddonPurchase({
          moduleId,
          cardNumber: (values.card_number || "").replace(/\s/g, ""),
          ccOwner: values.cc_owner,
          expiryMonth: values.expiry_month,
          expiryYear: values.expiry_year,
          cvv: values.cvv,
        })

        const cType = resp.headers.get("content-type") || ""
        let htmlToRender = null

        if (cType.includes("application/json")) {
          const json = await resp.json()
          if (resp.ok && json.statusCode === 200) {
            htmlToRender = json.data
          } else {
            setErrorMessage(json.message || "Ödeme başlatılamadı")
            setSubmitting(false)
            return
          }
        } else {
          htmlToRender = await resp.text()
        }

        if (htmlToRender) {
          document.open("text/html", "replace")
          document.write(htmlToRender)
          document.close()
        }
      } catch (err) {
        setErrorMessage(err.message || "Beklenmeyen bir hata oluştu")
        setSubmitting(false)
      }
    },
  })

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center p-5">
          <Spinner size="lg" color="primary" />
          <div className="mt-3 text-muted">Modül bilgisi yükleniyor...</div>
        </div>
      </div>
    )
  }

  const grants = []
  if (module?.grantsMaxAccounts) grants.push(`+${module.grantsMaxAccounts} cari / bayi`)
  if (module?.grantsMaxUsers) grants.push(`+${module.grantsMaxUsers} kullanıcı`)
  if (module?.grantsCollectionAmount) grants.push(`+${formatPrice(module.grantsCollectionAmount)} tahsilat`)
  if (module?.grantsCollectionCount) grants.push(`+${module.grantsCollectionCount} tahsilat adedi`)
  if ((module?.moduleFeatures || []).length > 0) grants.push(`${module.moduleFeatures.length} ek özellik`)
  ;(module?.moduleIntegrations || []).forEach((code) =>
    grants.push(`${integrationLabels[code] || code} entegrasyonu`)
  )

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-3">
          <BackButton />
          <h5 className="mb-0 ms-2 fw-semibold" style={{ color: NAVY }}>Ek Modül Ödemesi</h5>
        </div>

        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

        {!module ? (
          <TableCard>
            <CardBody className="p-4 text-center">
              <i className="bx bx-error-circle text-muted" style={{ fontSize: 48 }} />
              <h6 className="mt-3 mb-1">Modül bulunamadı</h6>
              <Button color="light" className="mt-2" onClick={() => navigate("/subscription/addons")}>
                360 Market'e Dön
              </Button>
            </CardBody>
          </TableCard>
        ) : (
          <Row>
            <Col lg={7}>
              <TableCard>
                <CardBody className="p-4">
                  <h6 className="fw-semibold mb-3">Kart Bilgileri</h6>

                  <FormGroup>
                    <Label>Kart Numarası</Label>
                    <Input
                      name="card_number"
                      inputMode="numeric"
                      maxLength={23}
                      placeholder="0000 0000 0000 0000"
                      value={formik.values.card_number}
                      onChange={(e) => formik.setFieldValue("card_number", formatCardNumber(e.target.value))}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.card_number && !!formik.errors.card_number}
                    />
                    <FormFeedback>{formik.errors.card_number}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Kart Üzerindeki İsim</Label>
                    <Input
                      name="cc_owner"
                      value={formik.values.cc_owner}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.cc_owner && !!formik.errors.cc_owner}
                    />
                    <FormFeedback>{formik.errors.cc_owner}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col xs={4}>
                      <FormGroup>
                        <Label>Ay</Label>
                        <Input
                          name="expiry_month"
                          inputMode="numeric"
                          maxLength={2}
                          placeholder="12"
                          value={formik.values.expiry_month}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.expiry_month && !!formik.errors.expiry_month}
                        />
                        <FormFeedback>{formik.errors.expiry_month}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col xs={4}>
                      <FormGroup>
                        <Label>Yıl</Label>
                        <Input
                          name="expiry_year"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="30"
                          value={formik.values.expiry_year}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.expiry_year && !!formik.errors.expiry_year}
                        />
                        <FormFeedback>{formik.errors.expiry_year}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col xs={4}>
                      <FormGroup>
                        <Label>CVV</Label>
                        <Input
                          name="cvv"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="000"
                          value={formik.values.cvv}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.cvv && !!formik.errors.cvv}
                        />
                        <FormFeedback>{formik.errors.cvv}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="text-muted" style={{ fontSize: ".76rem" }}>
                    <i className="bx bx-shield-check me-1" />
                    Kart bilgileriniz kaydedilmez; ödeme 3D Secure ile PayTR üzerinden alınır.
                  </div>
                </CardBody>
              </TableCard>
            </Col>

            <Col lg={5}>
              <TableCard>
                <CardBody className="p-4">
                  <h6 className="fw-semibold mb-3">Özet</h6>

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" style={{ fontSize: ".85rem" }}>Modül</span>
                    <span className="fw-semibold text-end">{module.name}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" style={{ fontSize: ".85rem" }}>Tip</span>
                    <span style={{ fontSize: ".85rem" }}>{module.lifecycleLabel}</span>
                  </div>

                  {grants.length > 0 && (
                    <div className="p-3 rounded-3 mb-3" style={{ background: "#F7F9FC" }}>
                      <div className="text-muted mb-1" style={{ fontSize: ".76rem" }}>
                        Paketinize eklenecek haklar
                      </div>
                      {grants.map((g) => (
                        <div key={g} style={{ fontSize: ".82rem" }}>
                          <i className="bx bx-check me-1" style={{ color: "#71C30B" }} />{g}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-top pt-3">
                    <div className="d-flex justify-content-between" style={{ fontSize: ".85rem" }}>
                      <span className="text-muted">KDV ({module.vatRate}%)</span>
                      <span>{formatPrice(module.vatAmount)}</span>
                    </div>
                    <div className="d-flex justify-content-between fw-semibold mt-1">
                      <span>Toplam</span>
                      <span className="h5 mb-0" style={{ color: NAVY }}>{formatPrice(module.totalPrice)}</span>
                    </div>
                  </div>

                  <Button
                    className="w-100 mt-3"
                    style={{ background: NAVY, borderColor: NAVY, color: "#fff", borderRadius: 999 }}
                    onClick={formik.handleSubmit}
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? <Spinner size="sm" /> : "Ödemeyi Tamamla"}
                  </Button>
                </CardBody>
              </TableCard>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}

export default AddonCheckout
